/**
 * Dates, in the clinic's own time.
 *
 * Everything the panel stores is ISO 8601 UTC — that is what `content/` holds
 * and what `lib/content-rules.mjs` checks. Everything the panel *shows* or
 * *asks for* is America/Vancouver, whatever the editor's laptop is set to: a
 * Publisher scheduling an article for "Tuesday at nine" means nine in the
 * clinic, and a browser in another zone (or a phone that follows the person on
 * holiday) must not quietly move it.
 *
 * British Columbia is on permanent UTC−7 since 2026-03-09 (IANA tzdata 2026b);
 * nothing here assumes that either way — the runtime's zone data decides, and
 * a runtime with older data (Node bundles its own, see process.versions.tz)
 * will differ from CI by an hour for wall times after 2026-11-01.
 * No library. `Intl.DateTimeFormat` knows the zone's rules, including daylight
 * saving, and that is the only hard part; the rest is arithmetic on the parts
 * it gives back. Shared by the browser and the API, like forms.js.
 */

export const TIME_ZONE = "America/Vancouver";

const pad = (n) => String(n).padStart(2, "0");

/** The wall-clock parts of an instant in the clinic's zone. */
function wallClock(date) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TIME_ZONE,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(date);
  const value = (type) => Number(parts.find((p) => p.type === type)?.value);
  // Some engines print midnight as "24" under h23; keep it on the day it is.
  return { year: value("year"), month: value("month"), day: value("day"), hour: value("hour") % 24, minute: value("minute"), second: value("second") };
}

/** A parsed Date, or null for nothing and for nonsense. */
function instant(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `2026-09-28` — the day, in Vancouver. Empty for nothing. */
export function formatDay(iso) {
  const date = instant(iso);
  if (!date) return "";
  const w = wallClock(date);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
}

/** `2026-09-28 14:05` — the minute, in Vancouver. Empty for nothing. */
export function formatDateTime(iso) {
  const date = instant(iso);
  if (!date) return "";
  const w = wallClock(date);
  return `${formatDay(date)} ${pad(w.hour)}:${pad(w.minute)}`;
}

/** An ISO timestamp as `<input type="datetime-local">` wants it, in Vancouver. */
export function toLocalInput(iso) {
  const date = instant(iso);
  if (!date) return "";
  const w = wallClock(date);
  return `${formatDay(date)}T${pad(w.hour)}:${pad(w.minute)}`;
}

/**
 * How far the clinic's clock is ahead of UTC at an instant, in milliseconds
 * (negative: Vancouver is seven or eight hours behind).
 */
function offsetAt(ms) {
  const w = wallClock(new Date(ms));
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - ms;
}

/**
 * A `datetime-local` value (`2026-09-28T14:05`), read as Vancouver wall time,
 * as the ISO UTC timestamp the content stores. Null for anything else.
 *
 * The zone's offset depends on the instant, and the instant is what is being
 * looked for; two passes settle it. Across the spring-forward gap (02:00 to
 * 03:00 does not exist) a time in the gap lands an hour later, which is what
 * a clock on the wall does too.
 */
export function parseLocalInput(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(value || ""));
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match.map(Number);
  const asIfUtc = Date.UTC(y, mo - 1, d, h, mi, s || 0);
  // Date.UTC rolls a 13th month or a 40th day over into the next; a browser's
  // control never produces one, but a value pasted into the API might.
  const back = new Date(asIfUtc);
  if (back.getUTCMonth() !== mo - 1 || back.getUTCDate() !== d || back.getUTCHours() !== h || back.getUTCMinutes() !== mi) return null;
  let ms = asIfUtc - offsetAt(asIfUtc);
  ms = asIfUtc - offsetAt(ms);
  return new Date(ms).toISOString();
}

/**
 * A `<input type="date">` value (`2026-06-30`), read as a whole day in
 * Vancouver, as the ISO UTC timestamp of the day's last second — 23:59:59
 * Pacific. A closing date or a consent date means the day, not a moment:
 * "applications close June 30" is open through June 30 in the clinic, so the
 * build's clock (lib/careers-closed.js) closes it at the first rebuild after
 * that. Null for anything else.
 */
export function parseDayInput(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  return match ? parseLocalInput(`${match[0]}T23:59:59`) : null;
}

/** An ISO timestamp as `<input type="date">` wants it: the Vancouver day. */
export const toDayInput = (iso) => formatDay(iso);

/**
 * What the date control says under a moment still to come. The rebuild that
 * publishes a scheduled Item is `scheduled-publish.yml`'s hourly cron, which
 * GitHub runs when it gets round to it: 38 runs in 164 hours, gaps of 2.2 to
 * 7.2 hours (brief §6.2). Saying "every hour" here would have an editor
 * reporting a bug at ten past.
 */
export const SCHEDULED_NOTICE =
  "Scheduled. It stays hidden until then and appears on its own when the site next rebuilds — " +
  "same day, typically 2–7 hours after the time.";

/** Whether a timestamp is still to come. */
export const isFuture = (iso) => {
  const date = instant(iso);
  return Boolean(date) && date.getTime() > Date.now();
};
